import { pickBy } from 'lodash';

const toTime = (value) => {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

// Returns null when the feature is off or the config lacks what the event needs to run.
const normalizeEventConfig = (config, defaultImages = {}) => {
  if (!config || typeof config !== 'object') return null;

  const startTime = toTime(config.startTime);
  const endTime = toTime(config.endTime);
  const rewardCodeThreshold = Number(config.rewardCodeThreshold);
  if (
    !config.eventId ||
    startTime === null ||
    endTime === null ||
    !(rewardCodeThreshold > 0)
  )
    return null;

  return {
    ...config,
    startTime,
    endTime,
    drawTime: toTime(config.drawTime) ?? endTime,
    rewardCodeThreshold,
    // The link ends up in an <a href>, so anything but https is dropped.
    livestreamUrl: /^https:\/\//i.test(config.livestreamUrl || '')
      ? config.livestreamUrl
      : '',
    images: { ...defaultImages, ...pickBy(config.images, Boolean) },
  };
};

const isEventRunning = (config, now = Date.now()) =>
  Boolean(config) && now >= config.startTime && now <= config.endTime;

// The detail page stays open after the event so users can follow the draw.
const isDetailPageOpen = (config, now = Date.now()) =>
  Boolean(config) && now >= config.startTime && now <= config.drawTime;

const sumAccumulation = (orders = []) =>
  orders.reduce((total, order) => {
    const amount = Number(order?.actualPrice);
    return amount > 0 ? total + amount : total;
  }, 0);

// Free orders (`actualPrice` 0) do not count as transactions toward the event.
const countPaidTransactions = (orders = []) =>
  orders.filter((order) => Number(order?.actualPrice) > 0).length;

const calcAmountToNextCode = (total, threshold) =>
  threshold - (total % threshold);

// Rounds up: the backend ceils USD→VND, so the shown amount must reach the VND it stands for.
const vndToUsd = (vndAmount, usdToVndRate) =>
  vndAmount > 0 ? Math.ceil((vndAmount * 100) / usdToVndRate - 1e-9) / 100 : 0;

// The API sends codes with the leading `#` already; the guard keeps older data readable.
const formatRewardCode = (code = '') =>
  code.startsWith('#') ? code : `#${code}`;

// Websocket `rewardCodes` is `[{ eventId, code }]` and only carries this payment's new codes.
const pickEventRewardCodes = (rewardCodes, eventId) =>
  (Array.isArray(rewardCodes) ? rewardCodes : [])
    .filter((item) => item?.code && item.eventId === eventId)
    .map((item) => item.code);

export {
  normalizeEventConfig,
  isEventRunning,
  isDetailPageOpen,
  sumAccumulation,
  countPaidTransactions,
  calcAmountToNextCode,
  vndToUsd,
  formatRewardCode,
  pickEventRewardCodes,
};
